var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _FifoLogger_instances, _FifoLogger_addEvent;
import { v4 } from '@lukeed/uuid';
import { TypedEventTarget } from 'typescript-event-target';
import { ChangeEvent, LogEvent } from './events';
import { levels } from './levels';
/**
 * A FIFO logger that stores the last events in an array.
 */
export class FifoLogger extends TypedEventTarget {
    constructor(options = {}) {
        super();
        _FifoLogger_instances.add(this);
        this.lastID = { id: 0 };
        this.initialOptions = options;
        this.uuids = [v4()];
        this.events = [];
        this.level = options.level || 'info';
        this.levelAsNumber = levels.values[this.level];
        this.limit = options.limit ?? 1000;
        this.bindings = options.bindings ?? {};
    }
    setLevel(level) {
        this.level = level;
        this.levelAsNumber = levels.values[level];
    }
    setLimit(limit) {
        this.limit = limit;
        this.checkSize();
    }
    checkSize() {
        if (this.events.length > this.limit) {
            this.events.splice(0, this.events.length - this.limit);
        }
    }
    /**
     * Remove events from the current logger and its children.
     */
    clear() {
        for (let i = this.events.length - 1; i >= 0; i--) {
            if (this.events[i].uuids.includes(this.uuids[0])) {
                this.events.splice(i, 1);
            }
        }
        this.dispatchTypedEvent('change', new ChangeEvent({
            logs: this.events,
            info: { depth: this.uuids.length },
        }));
    }
    /**
     * Get a filtered list of all log events.
     * @param options
     */
    getLogs(options = {}) {
        const { level, minLevel, includeChildren } = options;
        let logs = this.events.slice();
        if (includeChildren) {
            logs = logs.filter((log) => log.uuids.includes(this.uuids[0]));
        }
        else {
            logs = logs.filter((log) => log.uuids[0] === this.uuids[0]);
        }
        if (level) {
            const levelNumber = Number(levels.values[level]);
            if (Number.isNaN(levelNumber)) {
                throw new Error('Invalid level');
            }
            logs = logs.filter((log) => log.level === levelNumber);
        }
        if (minLevel) {
            const levelNumber = Number(levels.values[minLevel]);
            if (Number.isNaN(levelNumber)) {
                throw new Error('Invalid level');
            }
            logs = logs.filter((log) => log.level >= levelNumber);
        }
        return logs;
    }
    /**
     * @param bindings an object of key-value pairs to include in log lines as properties.
     */
    child(bindings) {
        const newFifoLogger = new FifoLogger(this.initialOptions);
        newFifoLogger.events = this.events;
        newFifoLogger.uuids = [v4(), ...this.uuids];
        newFifoLogger.lastID = this.lastID;
        newFifoLogger.bindings = { ...this.bindings, ...bindings };
        newFifoLogger.addEventListener('change', (event) => {
            this.dispatchTypedEvent('change', new ChangeEvent(event.detail));
        });
        return newFifoLogger;
    }
    trace(value, message) {
        __classPrivateFieldGet(this, _FifoLogger_instances, "m", _FifoLogger_addEvent).call(this, levels.values.trace, value, message);
    }
    debug(value, message) {
        __classPrivateFieldGet(this, _FifoLogger_instances, "m", _FifoLogger_addEvent).call(this, levels.values.debug, value, message);
    }
    info(value, message) {
        __classPrivateFieldGet(this, _FifoLogger_instances, "m", _FifoLogger_addEvent).call(this, levels.values.info, value, message);
    }
    warn(value, message) {
        __classPrivateFieldGet(this, _FifoLogger_instances, "m", _FifoLogger_addEvent).call(this, levels.values.warn, value, message);
    }
    error(value, message) {
        __classPrivateFieldGet(this, _FifoLogger_instances, "m", _FifoLogger_addEvent).call(this, levels.values.error, value, message);
    }
    fatal(value, message) {
        __classPrivateFieldGet(this, _FifoLogger_instances, "m", _FifoLogger_addEvent).call(this, levels.values.fatal, value, message);
    }
}
_FifoLogger_instances = new WeakSet(), _FifoLogger_addEvent = function _FifoLogger_addEvent(level, value, message) {
    if (level < this.levelAsNumber)
        return;
    const event = {
        id: ++this.lastID.id,
        level,
        levelLabel: levels.labels[level],
        time: Date.now(),
        uuids: this.uuids,
        message: '',
    };
    if (value instanceof Error) {
        event.message = value.toString();
        event.error = value;
        event.meta = { ...this.bindings };
    }
    else if (message !== undefined && typeof value === 'object') {
        event.message = message;
        event.meta = { ...this.bindings, ...value };
    }
    else if (message === undefined && typeof value === 'string') {
        event.message = value;
        event.meta = { ...this.bindings };
    }
    else {
        throw new Error('Invalid arguments');
    }
    this.events.push(event);
    this.checkSize();
    this.dispatchTypedEvent('log', new LogEvent({
        log: event,
        logs: this.events,
        info: { depth: this.uuids.length },
    }));
    this.dispatchTypedEvent('change', new ChangeEvent({
        logs: this.events,
        info: { depth: this.uuids.length },
    }));
};
//# sourceMappingURL=FifoLogger.js.map